import { useCallback, useEffect, useRef, useState } from 'react';
import type { GoogleProfile } from '../../global';

/**
 * Oturum açmış Google hesabının adı/e-postası/fotoğrafı.
 *
 * Profil, ana süreçte şifreli oturum dosyasında saklanıyor ve fotoğraf data
 * URI olarak gömülü geliyor — yani burada ağ isteği yok, renderer Google'a
 * hiç bağlanmıyor.
 *
 * Takvim bağlantısı kesilip yeniden kurulduğunda bu kancanın da haberi olması
 * gerekiyor; `window` üzerinden yayılan hafif bir olayla senkron kalıyor.
 */
const PROFILE_CHANGED_EVENT = 'zabit:google-profile-changed';

export function notifyGoogleProfileChanged(): void {
  window.dispatchEvent(new CustomEvent(PROFILE_CHANGED_EVENT));
}

export function useGoogleAccount() {
  const [profile, setProfile] = useState<GoogleProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const { connected, profile: loaded } = await window.zabit.getGoogleCalendarStatus();
      if (!mountedRef.current) return;
      setProfile(connected && loaded ? loaded : null);
    } catch {
      if (mountedRef.current) setProfile(null);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();

    const handler = () => void load();
    window.addEventListener(PROFILE_CHANGED_EVENT, handler);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(PROFILE_CHANGED_EVENT, handler);
    };
  }, [load]);

  return { profile, isLoading, reload: load };
}
